import {
	BaseObservablePageCollectionController,
	IBaseResourceModel,
	IDictionary,
	IUser,
	PagedViewModel,
	UserSessionContext,
	asApiError,
} from '@ViewModels';
import { action, computed, makeObservable, observable } from 'mobx';

interface IUserDailyActivitySummaryRequest {
	userId?: string;
	startDate?: string;
	endDate?: string;
	team?: string;
}

interface IScore {
	dials?: number;
	connections?: number;
	demos?: number;
	points?: number;
}

export interface IGoals extends IScore {
	maxColdDemos?: number;
	maxFollowUps?: number;
	maxResearch?: number;
	maxOldies?: number;
	adjustedDials?: number;
	adjustedDemos?: number;
}

interface IVertical {
	industry?: string;
	subverticals?: string[];
}

interface IUserDailyActivity {
	/** Formatted like "yyyy-MM-dd" Example: 2021-09-10 */
	day?: string;
	score?: IScore;
	projectedScore?: IScore;
	goals?: IGoals;
	lastLeadServedId?: string;
	points?: number;
	activities?: IDictionary<number>;
	vertical?: IVertical;
	role?: string;
	teams?: string[];
	managerUserId?: string;
}

/**
 * All outcomes and skips are tracked per user per day and are available in the database. Other information about the
 * user, like what vertical they are on and who is their manager are also available. Score for the user is tracked here,
 * and how much progress they have made towards their goal in a given day.
 */
export class UserDailyActivity implements IUserDailyActivity, IBaseResourceModel {
	constructor() {
		makeObservable(this, {
			id: computed,
			day: observable,
			score: observable,
			projectedScore: observable,
			goals: observable,
			lastLeadServedId: observable,
			points: observable,
			activities: observable,
			vertical: observable,
			role: observable,
			teams: observable,
			managerUserId: observable,
			creationDate: observable,
			creator: observable,
			lastModifiedDate: observable,
		});
	}

	public get id() {
		return `${this.day}`;
	}
	public day?: string;
	public score?: IScore;
	public projectedScore?: IScore;
	public goals?: IGoals;
	public lastLeadServedId?: string;
	public points?: number;
	public activities?: IDictionary<number>;
	public vertical?: IVertical;
	public role?: string;
	public teams?: string[];
	public managerUserId?: string;
	public creationDate?: string;
	public creator?: Partial<IUser>;
	public lastModifiedDate?: string;
}

export class GoalsViewModel extends PagedViewModel<IUserDailyActivity, UserDailyActivity> {
	protected declare mPagedCollectionController: BaseObservablePageCollectionController<
		IUserDailyActivity,
		UserDailyActivity
	>;
	public startDate: Date = new Date();
	public endDate: Date = new Date();
	public user: Partial<IUser>;
	public pageSize: number;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			startDate: observable,
			endDate: observable,
			user: observable,
			pageSize: observable,
			request: computed,
			upload: action,
		});

		this.mPagedCollectionController = new BaseObservablePageCollectionController<IUserDailyActivity, UserDailyActivity>(
			{
				apiPath: this.composeApiUrl({ urlPath: 'userDailyActivity/summary' }),
				client: this.userSession.webServiceHelper,
				httpMethod: 'GET',
				transformer: x => x,
			}
		);
	}

	public get request(): IUserDailyActivitySummaryRequest {
		const request = {
			endDate: this.endDate.toISOString(),
			startDate: this.startDate.toISOString(),
			userId: this.user?.id,
		};

		return request;
	}

	public upload = async (file: File) => {
		const formData = new FormData();
		formData.append('files', file);

		const result = await this.userSession.webServiceHelper.callWebServiceAsync('aida/goals', 'PUT', formData);
		if (!result.success) {
			throw asApiError(result);
		}

		return result;
	};
}
